import { CREDIT_TOP_UP_EVENT } from "../../lib/creditTopUp";
import CreditMonthPicker from "./CreditMonthPicker";
import { Modal } from "antd";
import { creditsForCustomerCharge, FEATURE_RUN_COSTS, runsForCredits, TRACE_CREDITS } from "../../config/traceCredits";
import { useEffect, useState } from "react";
import { aiUsageFetch } from "../../lib/aiUsageApi";
import "./MonthlyAiUsage.css";
import { creditBillingRequest } from "../../lib/creditBillingApi";
import { useAuth } from "../../contexts/AuthContext";
import { isEmbroideryTrace } from "../../config/appVariant";

const ACCOUNT_USAGE_API = isEmbroideryTrace
  ? (process.env.REACT_APP_EMBROIDERY_AI_USAGE_API || "https://us-central1-svgtrace-pro.cloudfunctions.net/satinApi/usage")
  : (process.env.REACT_APP_AI_USAGE_API || "https://us-central1-svgtrace-pro.cloudfunctions.net/getAiUsage");

const SERVICE_LABELS = {
  retrace: "Retrace", "satin-ai": "Satin AI", generate: "Generate", modify: "Modify",
  analysis: "Image & stitch analysis", "auto-digitize": "Auto-digitize", centerline: "Centerline", other: "Other AI usage",
};

export default function MonthlyAiUsage({ placement = "sidebar" }) {
  const { user, userIsPremium, userIsEmbroideryPremium } = useAuth();
  const currentMonth = new Date().toISOString().slice(0,7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const [activeSection, setActiveSection] = useState("shop");
  const [open, setOpen] = useState(false);
  const [topUpTool, setTopUpTool] = useState('');
  const [selectedPackId, setSelectedPackId] = useState('credits-10');
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !isEmbroideryTrace) return;
    const tool = new URLSearchParams(window.location.search).get('previewCreditTopUp');
    if (!['Retrace', 'Satin', 'Modify'].includes(tool)) return;
    setTopUpTool(tool);
    setActiveSection('shop');
    setOpen(true);
  }, []);
  useEffect(() => {
    if (!isEmbroideryTrace || !user?.uid) return;
    const showShop = event => {
      // Only one mounted shop should handle the request.
      if (event.defaultPrevented) return;
      event.preventDefault();
      setTopUpTool(event.detail.tool);
      setActiveSection('shop');
      setPurchaseMessage('');
      setPurchaseError('');
      setAttempt(n => n + 1);
      setOpen(true);
    };
    window.addEventListener(CREDIT_TOP_UP_EVENT, showShop);
    return () => window.removeEventListener(CREDIT_TOP_UP_EVENT, showShop);
  }, [user?.uid]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt,setAttempt]=useState(0);
  const available = !!user;
  const [packs, setPacks] = useState([]);
  const [buying, setBuying] = useState("");
  const [purchaseMessage, setPurchaseMessage] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [confirmationAttempt, setConfirmationAttempt] = useState(0);
  useEffect(() => {
    setPacks([]);
    if (!user?.uid || !isEmbroideryTrace) return;
    const controller = new AbortController();
    creditBillingRequest('/packs', null, controller.signal)
      .then(result => { if (!controller.signal.aborted) setPacks(result.packs || []); })
      .catch(error => { if (!controller.signal.aborted) setPurchaseError(error.message); });
    return () => controller.abort();
  }, [user?.uid, open]);
  useEffect(() => {
    if (!user?.uid || !isEmbroideryTrace) return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('credit_checkout');
    if (!status) return;
    setOpen(true); setActiveSection('shop');
    const clearReturn = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('credit_checkout'); url.searchParams.delete('credit_session');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    };
    if (status === 'cancel') {
      setPurchaseMessage('Checkout canceled. No credits were added.'); clearReturn(); return;
    }
    const sessionId = params.get('credit_session');
    if (!sessionId) { setPurchaseError('Missing checkout reference. Please contact support if you paid.'); return; }
    const controller = new AbortController();
    let timer;
    let attempts = 0;
    const confirm = async () => {
      setPurchaseError(''); setPurchaseMessage('Confirming your payment…');
      try {
        const result = await creditBillingRequest('/confirm', { sessionId }, controller.signal);
        if (controller.signal.aborted) return;
        if (result.status === 'credited') {
          setPurchaseMessage(`${Number(result.credits).toLocaleString()} credits added to your account.`);
          setAttempt(n => n + 1); clearReturn();
        } else if (++attempts < 10) {
          setPurchaseMessage('Payment is still processing. Your credits will appear once it completes.');
          timer = setTimeout(confirm, 3000);
        } else {
          setPurchaseMessage('Payment is still processing. You can check again shortly.');
        }
      } catch (error) {
        if (!controller.signal.aborted) { setPurchaseMessage(''); setPurchaseError(error.message); }
      }
    };
    confirm();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [user?.uid, confirmationAttempt]);
  const buyCredits = async pack => {
    if (buying) return;
    setBuying(pack.id); setPurchaseError(''); setPurchaseMessage('Opening secure checkout…');
    try {
      const result = await creditBillingRequest('/checkout', { packId: pack.id, returnUrl: window.location.href });
      const checkout = new URL(result.url);
      if (checkout.protocol !== 'https:' || checkout.hostname !== 'checkout.stripe.com') throw new Error('Checkout returned an invalid link.');
      window.location.assign(checkout.href);
    } catch (error) {
      setPurchaseError(error.message); setPurchaseMessage(''); setBuying('');
    }
  };
  useEffect(() => { setData(null); }, [user?.uid]);
  useEffect(() => {
    if (!available) return;
    const controller=new AbortController();
    setError("");

    const base=process.env.REACT_APP_SATIN_API || "http://127.0.0.1:8767";
    (async () => {
      const url = ACCOUNT_USAGE_API;
      return url
        ? aiUsageFetch(url, {signal:controller.signal})
        : aiUsageFetch(`${base}/usage`,{signal:controller.signal});
    })()
      .then(async r => {if (!r.ok) throw new Error("Usage is unavailable. Please try again."); return r.json();})
      .then(d => {if (!controller.signal.aborted) setData(d);})
      .catch(e => {if (!controller.signal.aborted) setError(e.message);});
    return () => controller.abort();
  },[open,attempt,user?.uid,available]);
  useEffect(() => {
    const refresh = () => { if (!document.hidden) setAttempt(n => n + 1); };
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);
  useEffect(() => {
    if (selectedMonth === currentMonth || !open || activeSection !== "usage") return;
    const controller = new AbortController();
    setHistory(null); setHistoryError("");
    (async () => {
      const endpoint = ACCOUNT_USAGE_API;
      const url = new URL(endpoint || `${process.env.REACT_APP_SATIN_API || "http://127.0.0.1:8767"}/usage`, window.location.origin);
      url.searchParams.set("month", selectedMonth);
      const response = endpoint ? await aiUsageFetch(url.toString(), {signal:controller.signal}) : await aiUsageFetch(url.toString(), {signal:controller.signal});
      if (!response.ok) throw new Error("Usage for this month is unavailable.");
      const result = await response.json();
      if (result.periodStart?.slice(0,7) !== selectedMonth) throw new Error("Monthly history is not connected yet.");
      if (!controller.signal.aborted) setHistory(result);
    })().catch(e => { if (!controller.signal.aborted) setHistoryError(e.message); });
    return () => controller.abort();
  }, [selectedMonth, currentMonth, open, activeSection, attempt, user?.uid]);
  const usageData = selectedMonth === currentMonth ? data : history;
  const usageError = selectedMonth === currentMonth ? error : historyError;
  if (!available) return null;
  const count=n => Number(n || 0).toLocaleString(undefined, {maximumFractionDigits:0});
  const packMultiple = pack => `${Number((pack.credits / TRACE_CREDITS.proMonthlyIncluded).toFixed(2)).toLocaleString()}× monthly Pro credits`;
  // In the top-up flow the pack is judged against the blocked tool, so lead with
  // runs of that tool and keep the allowance multiple for the general shop.
  const featureCost = FEATURE_RUN_COSTS[topUpTool];
  // Thread packaging, smallest to largest: the sizes an embroiderer already buys.
  const PACK_TIERS = {1000: "Bobbin", 2500: "Spool", 5000: "Cone"};
  const packDetail = pack => {
    const runs = runsForCredits(pack.credits, topUpTool);
    // Only fixed-cost tools get a per-pack run count. A range on every card reads
    // as a promise the pack cannot keep, so variable tools fall back to the
    // allowance multiple and let the footnote explain the spread.
    return runs && runs.low === runs.high ? `${count(runs.low)} ${featureCost.noun}` : packMultiple(pack);
  };
  const accountBalance = !error && data?.scope === "account";
  const monthlyRemaining = accountBalance ? (data.monthlyCreditsRemaining ?? data.creditsRemaining) : null;
  const monthlyLeft = Number.isFinite(monthlyRemaining) ? Math.max(0, monthlyRemaining) : 0;
  const monthlyLeftPercent = Math.min(100, (monthlyLeft / TRACE_CREDITS.proMonthlyIncluded) * 100);
  const selectedPack = packs.find(pack => pack.id === selectedPackId);
  const purchasedRemaining = accountBalance ? data.purchasedCreditsRemaining : null;
  const renewalDate = data?.periodEnd && Number.isFinite(Date.parse(data.periodEnd))
    ? new Date(data.periodEnd).toLocaleDateString(undefined, {month:"short", day:"numeric", timeZone:"UTC"}) : null;
  return <>
    {/* "headless" mounts the top-up listener on screens that hide the sidebar. */}
    {placement !== "headless" && <button type="button" className={placement === "topbar" ? "monthly-ai-topbar" : "sidebar-item monthly-ai-toggle"} aria-haspopup="dialog" aria-expanded={open} onClick={() => { setTopUpTool(''); setOpen(true); }} title="AI credits">
      <span className="sidebar-item-icon" aria-hidden="true">✦</span>
      <span className={placement === "topbar" ? "" : "sidebar-item-label"}>{placement === "topbar" && data?.scope === "account" && Number.isFinite(data.creditsRemaining) ? count(data.creditsRemaining) : "AI credits"}
      {placement === "topbar" && data?.scope === "local-preview" && <small>Preview</small>}
      {placement === "topbar" && error && <small>Unavailable</small>}
      </span>
    </button>}
    <Modal title={topUpTool ? "Buy credits" : "AI credits"} open={open} onCancel={() => setOpen(false)} footer={null} centered zIndex={10000} width={820} className={`monthly-ai-shop${topUpTool ? " credit-sales-modal" : ""}`}>
      <div className="credit-shop-layout">
      {!topUpTool && <nav className="credit-shop-nav" aria-label="Credit dashboard sections">
        <button type="button" aria-pressed={activeSection === "shop"} onClick={() => setActiveSection("shop")}>Buy credits</button>
        <button type="button" aria-pressed={activeSection === "usage"} onClick={() => setActiveSection("usage")}>Usage &amp; allowance</button>
      </nav>}
      <div className="credit-shop-content">
      <div hidden={activeSection !== "shop"}>
      {!topUpTool && <div className="credit-shop-hero">
        <div className="credit-shop-balance">
          <span>YOUR BALANCE</span>
          <strong>{error ? "Unavailable" : !data ? "Loading…" : data.scope === "account" && Number.isFinite(data.creditsRemaining) ? `${count(data.creditsRemaining)} credits` : "Preview mode"}</strong>
          <dl className="credit-shop-breakdown" aria-label="Balance breakdown">
            {data?.creditsReserved > 0 && <div>
              <dt>Reserved credits<small>Held while final costs are pending; excluded from your available balance</small></dt>
              <dd>{count(data.creditsReserved)} credits</dd>
            </div>}
            <div>
              <dt>Monthly credits<small>{accountBalance && !data.allowanceActive ? "No monthly allowance" : renewalDate && accountBalance ? `Renews ${renewalDate} · Used first` : "Renews monthly · Used first"}</small></dt>
              <dd>{Number.isFinite(monthlyRemaining) ? Number.isFinite(data?.includedCredits) ? `${count(monthlyRemaining)} / ${count(data.includedCredits)}` : `${count(monthlyRemaining)} left` : "—"}</dd>
            </div>
            <div>
              <dt>Purchased credits<small>Never expire</small></dt>
              <dd>{Number.isFinite(purchasedRemaining) ? `${count(purchasedRemaining)} left` : "Unavailable"}</dd>
            </div>
          </dl>
        </div>
      </div>
      }
      {topUpTool && <div className="credit-sales-hero">
        <h2>Monthly credits are used up</h2>
        <p>They renew {renewalDate || "at the start of next month"}. Buy a pack to continue {topUpTool}.</p>
        <p className="credit-spool-readout">{count(monthlyLeft)} <span>of {count(TRACE_CREDITS.proMonthlyIncluded)} monthly credits left</span></p>
        <div className="credit-spool" aria-hidden="true">
          <span className="credit-spool-track"><span className="credit-spool-thread" style={{width: `${monthlyLeftPercent}%`}} /></span>
        </div>
        {Number.isFinite(purchasedRemaining) && <p className="credit-purchased-readout">{count(purchasedRemaining)} purchased credit{purchasedRemaining === 1 ? "" : "s"}</p>}
      </div>}
      {!topUpTool && <h3 className="credit-shop-title">Buy credits</h3>}
      {topUpTool ? <fieldset className="credit-amount-options">
        <legend className="credit-amount-legend">Credit amount</legend>
        {(packs.length ? packs : [10, 25, 50].map(dollars => ({id: `credits-${dollars}`, credits: dollars * TRACE_CREDITS.creditsPerDollar, amountCents: dollars * 100}))).map(pack => <label className={`credit-amount-option${selectedPackId === pack.id ? ' is-selected' : ''}`} key={pack.id}>
          <input type="radio" name="credit-pack" value={pack.id} checked={selectedPackId === pack.id} onChange={() => setSelectedPackId(pack.id)} disabled={!!buying} />
          {PACK_TIERS[pack.amountCents] && <span className="credit-amount-tier">{PACK_TIERS[pack.amountCents]}</span>}
          <strong>${pack.amountCents / 100} <small>USD</small></strong>
          <span className="credit-pack-credits">{count(pack.credits)} <small>credits</small></span>
          <span className="credit-pack-multiple">{packDetail(pack)}</span>
        </label>)}
      </fieldset> : <>
      <section className="credit-shop-packs" aria-label="Credit packs">
        {(packs.length ? packs : [10, 25, 50].map(dollars => ({id: `credits-${dollars}`, credits: dollars * TRACE_CREDITS.creditsPerDollar, amountCents: dollars * 100}))).map(pack => <div className="credit-shop-pack" key={pack.id}>
          {topUpTool ? <span className="credit-sales-pack-name">{pack.amountCents === 1000 ? 'A little boost' : pack.amountCents === 2500 ? 'Room to explore' : 'Keep the ideas coming'}</span> : <span aria-hidden="true">✦</span>}
          <strong>{count(pack.credits)}</strong>
          <small>credits</small>
          <span className="credit-pack-multiple">{packMultiple(pack)}</span>
          <p>{topUpTool ? (pack.amountCents === 1000 ? "Pick up where you left off." : pack.amountCents === 2500 ? "Try more variations." : "More room for your next projects.") : "One-time top-up"}</p>
          <button type="button" disabled={!isEmbroideryTrace || !packs.length || !!buying} onClick={() => buyCredits(pack)} aria-label={`Buy ${count(pack.credits)} credits for ${pack.amountCents / 100} US dollars`}>{buying === pack.id ? "Opening…" : `${topUpTool ? "Buy for " : ""}$${pack.amountCents / 100} USD`}</button>
        </div>)}
      </section></>}
      <p className="credit-pack-comparison">{featureCost
        ? featureCost.creditsLow
            ? `A ${topUpTool} layer costs ${count(featureCost.creditsLow)}–${count(featureCost.credits)} credits, based on the shape.`
            : `${topUpTool} costs about ${count(featureCost.credits)} credits.`
        : `Compared with the ${count(TRACE_CREDITS.proMonthlyIncluded)} credits included each month with Pro.`}</p>
      <p className="credit-shop-persistence">{topUpTool ? "One-time purchase. Credits do not expire and apply to Retrace, Satin, and Modify." : "Purchased credits never expire. Use them whenever you’re ready."}</p>
      {topUpTool && <div className="credit-sales-actions">
        <button type="button" className="credit-sales-later" onClick={() => setOpen(false)}>{renewalDate ? `Resume on ${renewalDate}` : 'Not now'}</button>
        <button type="button" className="credit-sales-continue" disabled={!selectedPack || !!buying} onClick={() => buyCredits(selectedPack)}>{buying ? 'Opening checkout…' : selectedPack ? `Buy ${count(selectedPack.credits)} credits` : 'Buy credits'}</button>
      </div>}
      {purchaseMessage && <p className="credit-shop-notice" role="status">{purchaseMessage}</p>}
      {purchaseError && <p className="credit-shop-notice" role="alert">{purchaseError}</p>}
      {isEmbroideryTrace && new URLSearchParams(window.location.search).has('credit_session') && <button type="button" onClick={() => setConfirmationAttempt(n => n + 1)}>Check payment status</button>}
      {!isEmbroideryTrace && <p className="credit-shop-notice">Credit packs are available in EmbroideryTrace.</p>}
      </div>
      <section className="credit-shop-usage" hidden={!!topUpTool || activeSection !== "usage"} aria-label="Usage and monthly allowance">
    <div className="monthly-ai-body">
      <div className="credit-month-selector"><span>Usage month</span><CreditMonthPicker value={selectedMonth} max={currentMonth} onChange={setSelectedMonth} /></div>
      <h3>{usageData?.allowanceActive ? "Your monthly allowance" : "Credits used"}</h3>
      {usageError ? <p role="alert">{usageError}</p> : !usageData ? <p role="status">Loading usage…</p> : <>
        {usageData.scope === "local-preview" && <p className="credit-preview-label">Local preview · Combined development usage.</p>}
        <div className="credit-usage-total"><span>Used in this period</span>
        <strong className="monthly-ai-amount">{count(usageData.creditsUsed ?? creditsForCustomerCharge(usageData.subtotalUsd))} <small>credits{usageData.complete ? "" : " · incomplete"}</small></strong>
        </div>
        {usageData.allowanceActive && <div className="monthly-ai-allowance">
          <div><span>Monthly EmbroideryTrace allowance</span><strong>{count(usageData.includedCredits)} credits</strong></div>
          {usageData.creditsReserved > 0 && <div><span>Reserved</span><strong>{count(usageData.creditsReserved)} credits</strong></div>}
          <div><span>{Number.isFinite(usageData.creditsReserved) ? "Available" : usageData.complete ? "Remaining" : "Remaining · incomplete"}</span><strong>{count(usageData.monthlyCreditsRemaining ?? usageData.creditsRemaining)} credits</strong></div>
          <progress aria-label="Monthly Pro credits used" max={usageData.includedCredits} value={Number.isFinite(usageData.monthlyCreditsRemaining) ? Math.max(0, usageData.includedCredits - usageData.monthlyCreditsRemaining) : Math.min(usageData.includedCredits, usageData.creditsUsed || 0)} />
          <p>Renews monthly. Unused monthly credits don’t roll over.</p>
        </div>}
        {usageData.scope === "local-preview" && userIsPremium !== "lifetime-pro" && userIsEmbroideryPremium && <p>EmbroideryTrace subscriptions include {count(TRACE_CREDITS.proMonthlyIncluded)} credits each month. Your account balance will appear when account tracking is connected.</p>}

        {usageData.accountingScope === "embroidery-stitch-workers" && <p>This balance covers the migrated EmbroideryTrace stitching workers. Older Generate, Modify, and Retrace usage is not included yet.</p>}
        <h4 className="monthly-ai-services-title">Usage by service</h4>
        {usageData.recordedRequests === 0 ? <div className="credit-usage-empty"><span aria-hidden="true">◇</span><p>No usage recorded this month.</p><small>Recorded AI activity will appear here.</small></div> : <dl className="monthly-ai-services" aria-label="Credits used by service">
          {(usageData.services?.length ? usageData.services : [{service:"other",creditsUsed:usageData.creditsUsed ?? creditsForCustomerCharge(usageData.subtotalUsd),pendingRequests:usageData.pendingRequests}])
            .slice().sort((a,b)=>b.creditsUsed-a.creditsUsed).map(row => <div key={row.service}>
              <dt>{SERVICE_LABELS[row.service] || SERVICE_LABELS.other}{!!row.pendingRequests && <small> · incomplete</small>}</dt>
              <dd>{count(row.creditsUsed)} credits</dd>
            </div>)}
        </dl>}
        {!!usageData.pendingRequests && <p>{usageData.creditsReserved > 0
          ? "Some final costs are pending. Reserved credits are not a final charge. Unused holds are released after reconciliation; refreshing alone may not resolve them."
          : "Some recorded calls are missing cost details. This total is incomplete; refreshing may not resolve it."}</p>}
        <p className="credit-period-note">Period ends: {new Date(usageData.periodEnd).toLocaleDateString(undefined,{month:"long",day:"numeric",timeZone:"UTC"})} · UTC calendar month.</p>
        {!usageData.trackingEnabled && <p className="credit-preview-note">Preview only. No credits are being deducted.</p>}
        <details className="credit-accounting-note"><summary>About these totals</summary><p>Monthly credits are used first. Purchased credits carry over until used. Recorded usage is charged once; later provider cost corrections do not add charges.</p></details>
      </>}
      <button type="button" onClick={() => setAttempt(attempt+1)}>Refresh usage</button>
    </div>
      </section>
      </div>
      </div>
    </Modal>
  </>;
}
